
import React, { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Subcategory {
  id: number;
  name: string;
  parent_id: number;
}

interface SubcategorySelectProps {
  value: string;
  onChange: (value: string) => void;
  categoryId: string | null;
  placeholder?: string;
  required?: boolean;
  label?: string;
}

const SubcategorySelect = ({
  value,
  onChange,
  categoryId,
  placeholder = "Zgjidhni nënkategorinë",
  required = false,
  label = "Nënkategoria",
}: SubcategorySelectProps) => {
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);

  const isValidCategoryId = (id: string | null): boolean => {
    if (!id) return false;
    const numId = parseInt(id, 10);
    return !isNaN(numId) && numId > 0;
  };

  // Fetch subcategories based on parent category
  const { data, isLoading, error } = useQuery({
    queryKey: ["subcategories", categoryId],
    queryFn: async () => {
      if (!isValidCategoryId(categoryId)) {
        return [];
      }

      // categoryId is now guaranteed to be a string representing a positive number
      const categoryIdNum = parseInt(categoryId as string, 10);

      const { data, error } = await supabase
        .from("subcategories")
        .select("*")
        .eq("parent_id", categoryIdNum)
        .order("name");

      if (error) {
        console.error("Error fetching subcategories:", error); // Log error
        throw error;
      }

      return data || [];
    },
    enabled: isValidCategoryId(categoryId), // Only enable if categoryId is valid
  });

  useEffect(() => {
    if (data) {
      setSubcategories(data);
    }
  }, [data]);

  // Only reset subcategory when category changes AND the value is not empty
  // This prevents an infinite loop of state changes
  useEffect(() => {
    // If categoryId becomes invalid (e.g., cleared), reset subcategory selection
    if (!isValidCategoryId(categoryId)) {
      if (value) { // Only call onChange if there's a current value to clear
         onChange("");
      }
      setSubcategories([]); // Clear local subcategories state
      return;
    }

    if (categoryId && value && subcategories.length > 0) {
      // Check if the current value belongs to the current category
      const valueExists = subcategories.some(
        (sub) => sub.id.toString() === value
      );
      if (!valueExists) {
        onChange("");
      }
    } else if (categoryId && !value && subcategories.length > 0) {
      // If category is selected, but subcategory is not, do nothing to prevent reset
      // (unless subcategories list itself has changed, handled by data effect)
    } else if (categoryId && subcategories.length === 0 && data?.length === 0) {
      // If category is selected but no subcategories are found (and data confirms this)
      // and there's an existing subcategory value, clear it.
      if (value) {
        onChange("");
      }
    }

  }, [categoryId, subcategories, value, onChange, data]);

  return (
    <Select
      value={value}
      onValueChange={onChange}
      disabled={isLoading || !isValidCategoryId(categoryId)}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {isLoading ? (
          <SelectItem value="loading" disabled>Duke ngarkuar...</SelectItem>
        ) : error ? (
          <SelectItem value="error" disabled>
            Gabim gjatë ngarkimit të nënkategorive
          </SelectItem>
        ) : !isValidCategoryId(categoryId) || subcategories.length === 0 ? (
          <SelectItem value="none" disabled>
            {isValidCategoryId(categoryId) ? "Nuk ka nënkategori të disponueshme" : "Zgjidhni një kategori fillimisht"}
          </SelectItem>
        ) : (
          <SelectGroup>
            {label && <SelectLabel>{label}</SelectLabel>}
            {subcategories.map((subcategory) => (
              <SelectItem
                key={subcategory.id}
                value={subcategory.id.toString()}
              >
                {subcategory.name}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
      </SelectContent>
    </Select>
  );
};

export default SubcategorySelect;
